import './styles.scss';

import { memo, useEffect, useMemo, useState } from 'react';

import Pagination from '../../../../../../components/pagination';
import TableGrid from '../../../../../../components/table-grid';
import { DownloadButton } from '../../../../../../components/download-button';
import { BacktesterStrategyData } from '../../../../../../providers/backtesting/api/backtester-config-api.types';
import { formatBacktesterNumericCell } from '../../../../../../providers/backtesting/format-backtester-numeric-cell';
import { BacktestTableView } from '../../shared/backtest-table-view';
import { downloadTableAsCsv } from '../../shared/download-table-csv';

const PAGE_SIZE = 200;

const COLUMNS = [
  { field: 'fieldId', headerName: '#', align: 'left' as const },
  { field: 'timestamp', headerName: 'Timestamp', align: 'left' as const, minWidth: 210 },
  { field: 'pv', headerName: 'PV', align: 'right' as const, minWidth: 140 },
  { field: 'optionPv', headerName: 'Option PV', align: 'right' as const, minWidth: 120 },
  { field: 'spot', headerName: 'Spot', align: 'right' as const, minWidth: 110 },
  { field: 'perp', headerName: 'Perp', align: 'right' as const, minWidth: 110 },
  { field: 'fundingPerHour', headerName: 'Funding 8h', align: 'right' as const, minWidth: 120 },
  { field: 'delta', headerName: 'Delta', align: 'right' as const, minWidth: 105 },
  { field: 'theta', headerName: 'Theta', align: 'right' as const, minWidth: 120 },
  { field: 'gamma', headerName: 'Gamma', align: 'right' as const, minWidth: 110 },
  { field: 'vega', headerName: 'Vega', align: 'right' as const, minWidth: 120 },
  { field: 'vol2w', headerName: 'Vol 2W', align: 'right' as const, minWidth: 105 },
  { field: 'vol1m', headerName: 'Vol 1M', align: 'right' as const, minWidth: 105 },
  { field: 'vol2m', headerName: 'Vol 2M', align: 'right' as const, minWidth: 105 },
  { field: 'vol3m', headerName: 'Vol 3M', align: 'right' as const, minWidth: 105 },
  { field: 'vol6m', headerName: 'Vol 6M', align: 'right' as const, minWidth: 105 },
  { field: 'indicator1', headerName: 'Indicator 1', align: 'right' as const, minWidth: 105 },
  { field: 'indicator2', headerName: 'Indicator 2', align: 'right' as const, minWidth: 105 },
  { field: 'indicator3', headerName: 'Indicator 3', align: 'right' as const, minWidth: 105 },
  { field: 'indicator4', headerName: 'Indicator 4', align: 'right' as const, minWidth: 105 },
  { field: 'indicator5', headerName: 'Indicator 5', align: 'right' as const, minWidth: 105 },
].map((column) => ({
  ...column,
  flex: 0,
  width: column.minWidth ?? 80,
}));

interface PNLReportTableProps {
  strategyData: BacktesterStrategyData;
  selectedStrategy: string;
  isLoading?: boolean;
}

export const PNLReportTable = memo(
  ({ strategyData, selectedStrategy, isLoading }: PNLReportTableProps) => {
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
      setCurrentPage(1);
    }, [selectedStrategy]);

    const selectedStrategyData = strategyData[selectedStrategy];
    const selectedReports = selectedStrategyData?.pnl_reports ?? [];

    const totalRows = selectedReports.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / PAGE_SIZE));

    useEffect(() => {
      if (currentPage > totalPages) {
        setCurrentPage(totalPages);
      }
    }, [currentPage, totalPages]);

    const pagedRowData = useMemo(() => {
      const pageStart = (currentPage - 1) * PAGE_SIZE;
      return selectedReports.slice(pageStart, pageStart + PAGE_SIZE).map((row, i) => ({
        fieldId: pageStart + i + 1,
        timestamp: row.timestamp,
        pv: formatBacktesterNumericCell(row, 'pv'),
        optionPv: formatBacktesterNumericCell(row, 'option_pv'),
        spot: formatBacktesterNumericCell(row, 'spot'),
        perp: formatBacktesterNumericCell(row, 'perp'),
        fundingPerHour: formatBacktesterNumericCell(row, 'funding_8h'),
        delta: formatBacktesterNumericCell(row, 'delta'),
        theta: formatBacktesterNumericCell(row, 'theta'),
        gamma: formatBacktesterNumericCell(row, 'gamma'),
        vega: formatBacktesterNumericCell(row, 'vega'),
        vol2w: formatBacktesterNumericCell(row, 'vol_2W', 'vol_W2'),
        vol1m: formatBacktesterNumericCell(row, 'vol_1M', 'vol_M1'),
        vol2m: formatBacktesterNumericCell(row, 'vol_2M', 'vol_M2'),
        vol3m: formatBacktesterNumericCell(row, 'vol_3M', 'vol_M3'),
        vol6m: formatBacktesterNumericCell(row, 'vol_6M'),
        indicator1: formatBacktesterNumericCell(row, 'indicator_1'),
        indicator2: formatBacktesterNumericCell(row, 'indicator_2'),
        indicator3: formatBacktesterNumericCell(row, 'indicator_3'),
        indicator4: formatBacktesterNumericCell(row, 'indicator_4'),
        indicator5: formatBacktesterNumericCell(row, 'indicator_5'),
      }));
    }, [currentPage, selectedReports]);

    const handleDownload = () => {
      downloadTableAsCsv(
        `pnl-report-${selectedStrategy}.csv`,
        COLUMNS,
        selectedReports.map((row, i) => ({
          fieldId: i + 1,
          timestamp: row.timestamp,
          pv: formatBacktesterNumericCell(row, 'pv'),
          optionPv: formatBacktesterNumericCell(row, 'option_pv'),
          spot: formatBacktesterNumericCell(row, 'spot'),
          perp: formatBacktesterNumericCell(row, 'perp'),
          fundingPerHour: formatBacktesterNumericCell(row, 'funding_8h'),
          delta: formatBacktesterNumericCell(row, 'delta'),
          theta: formatBacktesterNumericCell(row, 'theta'),
          gamma: formatBacktesterNumericCell(row, 'gamma'),
          vega: formatBacktesterNumericCell(row, 'vega'),
          vol2w: formatBacktesterNumericCell(row, 'vol_2W', 'vol_W2'),
          vol1m: formatBacktesterNumericCell(row, 'vol_1M', 'vol_M1'),
          vol2m: formatBacktesterNumericCell(row, 'vol_2M', 'vol_M2'),
          vol3m: formatBacktesterNumericCell(row, 'vol_3M', 'vol_M3'),
          vol6m: formatBacktesterNumericCell(row, 'vol_6M'),
          indicator1: formatBacktesterNumericCell(row, 'indicator_1'),
          indicator2: formatBacktesterNumericCell(row, 'indicator_2'),
          indicator3: formatBacktesterNumericCell(row, 'indicator_3'),
          indicator4: formatBacktesterNumericCell(row, 'indicator_4'),
          indicator5: formatBacktesterNumericCell(row, 'indicator_5'),
        })),
      );
    };

    return (
      <BacktestTableView
        title="PNL Report"
        className="pnl-report-table"
        filters={
          <div className="backtest-table-view__filter-spacer">
            <DownloadButton
              onClick={handleDownload}
              disabled={isLoading || selectedReports.length === 0}
            />
          </div>
        }
        pagination={
          <Pagination
            total={totalRows}
            pageSize={PAGE_SIZE}
            page={currentPage}
            onPageChange={setCurrentPage}
          />
        }
      >
        <TableGrid columns={COLUMNS} rowData={pagedRowData} disableAutoSize />
      </BacktestTableView>
    );
  },
);

PNLReportTable.displayName = 'PNLReportTable';
